// lane-style.util.ts — colour and label rules for the transcript player's dock.
// Pure: the component only forwards its lanes.

import type { FindingKind } from './sync-findings.util';
import type { SyncMark } from './sync-findings.util';
import { mmss, type TimelineLane } from './transcript-timing.util';

/** One colour per human so two people overlapping read as two bars. */
const HUMAN_BARS = [
  'bg-sky-400/80 dark:bg-sky-500/70', 'bg-amber-400/80 dark:bg-amber-500/70',
  'bg-violet-400/80 dark:bg-violet-500/70', 'bg-rose-400/80 dark:bg-rose-500/70'];
const HUMAN_DOTS = ['bg-sky-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500'];

function humanIdx(lane: TimelineLane, humanLanes: TimelineLane[]): number {
  const i = humanLanes.findIndex((l) => l.name === lane.name);
  return Math.max(0, i) % HUMAN_BARS.length;
}

/** Fill colour for a lane's segments on its row. */
export function laneBarClass(lane: TimelineLane, humanLanes: TimelineLane[]): string {
  return lane.ai ? 'bg-emerald-400/80 dark:bg-emerald-500/70' : HUMAN_BARS[humanIdx(lane, humanLanes)];
}

/** Legend dot colour, matching the lane's bar colour. */
export function laneDotClass(lane: TimelineLane, humanLanes: TimelineLane[]): string {
  return lane.ai ? 'bg-emerald-500' : HUMAN_DOTS[humanIdx(lane, humanLanes)];
}

/** Tooltip for one bar: who, and the span they held. */
export function segTitle(lane: TimelineLane, seg: { at: number; end: number }): string {
  return `${lane.name} · ${mmss(seg.at)} – ${mmss(seg.end)}`;
}

/** Avatar initials for a speaker name. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

/** Chip / block classes per finding kind (red = missing text, amber = behaviour). */
export function findingClass(kind: FindingKind): string {
  switch (kind) {
    case 'untranscribed':
    case 'gated_onset':
    case 'guest_not_received':
      return 'border-rose-400 bg-rose-50 text-rose-700 dark:border-rose-500/60 dark:bg-rose-500/10 dark:text-rose-300';
    case 'phantom':
      return 'border-fuchsia-400 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-500/60 dark:bg-fuchsia-500/10 dark:text-fuchsia-300';
    case 'talked_over':
    case 'late':
      return 'border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-500/60 dark:bg-amber-500/10 dark:text-amber-300';
  }
}

/** How one mark is drawn on the Events row: a class, and whether it is a span. */
export function markClass(mark: SyncMark): string {
  const k = mark.kind;
  if (k === 'gate') return 'bg-rose-500/45';
  if (k === 'stt_send') return 'bg-indigo-400/50';
  if (k.startsWith('guest:')) return 'bg-amber-400/50';
  if (/_flush$|_honored$/.test(k)) return 'bg-emerald-500';
  if (k.startsWith('interrupt.')) return 'bg-rose-500';
  return 'bg-slate-400';
}

/** Short on-track label for a mark. */
export function markLabel(mark: SyncMark): string {
  const k = mark.kind;
  if (k === 'gate') return 'gate closed';
  if (k === 'stt_send') return 'stt window';
  if (k.startsWith('guest:')) return 'guest audio';
  return k.replace(/^interrupt\./, '');
}

/** Tooltip for one mark. */
export function markTitle(mark: SyncMark): string {
  const when = mark.t1 != null ? `${mmss(mark.t)} – ${mmss(mark.t1)}` : mmss(mark.t);
  const data = mark.data ? ' ' + JSON.stringify(mark.data) : '';
  return `${mark.kind} · ${when}${data}`;
}
